import React, {
  useContext,
  useCallback,
  forwardRef,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
} from 'react'
import { useField, useFormikContext } from 'formik'
import { useTheme } from 'tamagui'

import { set, parse } from 'date-fns'
import { format } from 'date-fns-tz'

import { FormFieldContext } from './shared'

import './FormInput.css'

export interface FormTimePickerRef {
  focus: () => void
}

export interface FormTimePickerProps {
  isActive?: boolean
  onFocus?: () => void
  onBlur?: () => void
}

export const FormTimePicker = forwardRef<
  FormTimePickerRef,
  FormTimePickerProps
>(({ isActive, onFocus, onBlur }, ref) => {
  const formContext = useFormikContext()
  const formFieldContext = useContext(FormFieldContext)
  const [field, _meta, helpers] = useField<Date>(formFieldContext.name)
  const theme = useTheme()

  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  const values = formContext.values as {
    timeZone: string
  }

  const formattedValue = useMemo(() => {
    return format(field.value, 'hh:mma', { timeZone: values.timeZone })
  }, [field.value, values.timeZone])

  const handleFocus = useCallback<React.FocusEventHandler<HTMLInputElement>>(
    (event) => {
      const { target } = event

      target.setSelectionRange(0, 2)
      setIsFocused(true)

      if (formFieldContext.onFocus) {
        formFieldContext.onFocus()
      }

      if (onFocus) {
        onFocus()
      }
    },
    [formFieldContext.onFocus, onFocus]
  )

  const handleBlur = useCallback(() => {
    setIsFocused(false)

    if (formFieldContext.onBlur) {
      formFieldContext.onBlur()
    }

    if (onBlur) {
      onBlur()
    }
  }, [formFieldContext.onBlur, onBlur])

  const setValue = useCallback(
    (newValue: string) => {
      try {
        const parsedTime = parse(newValue, 'hh:mma', field.value)

        if (!isNaN(parsedTime.getTime())) {
          void helpers.setValue(parsedTime)
        }
      } catch {
        // Ignore invalid partial time input while the user is typing.
      }

      return newValue
    },
    [field.value, helpers.setValue]
  )

  const handleChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const value = event.target.value

      setValue(value)
    },
    [setValue]
  )

  const handleKeyDown = useCallback<
    React.KeyboardEventHandler<HTMLInputElement>
  >(
    (event) => {
      const { key } = event
      const target = event.target as HTMLInputElement

      if (!target.selectionStart || !target.selectionEnd) {
        target.setSelectionRange(0, 2)
      }

      const selectionStart = target.selectionStart as number
      const selectionEnd = target.selectionEnd as number

      const selectedSegment = target.value.substring(
        selectionStart,
        selectionEnd
      )

      switch (key) {
        case 'ArrowUp': {
          event.preventDefault()
          event.stopPropagation()

          let newValue

          if (selectionStart < 3) {
            newValue = (parseInt(selectedSegment, 10) + 1) % 12 || 12

            if (newValue === 12) {
              const amOrPm = target.value.substring(5, 7)

              target.value = setValue(
                target.value.slice(0, selectionStart) +
                  '12:' +
                  target.value.slice(3, 5) +
                  (amOrPm === 'AM' ? 'PM' : 'AM')
              )

              target.setSelectionRange(selectionStart, selectionEnd)

              break
            }
          } else if (selectionStart < 5) {
            const parsedValue = parseInt(selectedSegment, 10)
            newValue = (parsedValue + (15 - (parsedValue % 15))) % 60
          } else {
            newValue = selectedSegment === 'AM' ? 'PM' : 'AM'
          }

          target.value = setValue(
            target.value.slice(0, selectionStart) +
              newValue.toString().padStart(2, '0') +
              target.value.slice(selectionEnd)
          )

          target.setSelectionRange(selectionStart, selectionEnd)

          break
        }
        case 'ArrowDown': {
          event.preventDefault()
          event.stopPropagation()

          let newValue

          if (selectionStart < 3) {
            newValue = parseInt(selectedSegment, 10) - 1 || 12

            if (newValue === 11) {
              const amOrPm = target.value.substring(5, 7)

              target.value = setValue(
                target.value.slice(0, selectionStart) +
                  '11:' +
                  target.value.slice(3, 5) +
                  (amOrPm === 'PM' ? 'AM' : 'PM')
              )

              target.setSelectionRange(selectionStart, selectionEnd)

              break
            }
          } else if (selectionStart < 5) {
            const parsedValue = parseInt(selectedSegment, 10)
            newValue =
              selectedSegment === '00'
                ? 45
                : Math.max(parsedValue - (15 - (parsedValue % 15)), 0)
          } else {
            newValue = selectedSegment === 'AM' ? 'PM' : 'AM'
          }

          target.value = setValue(
            target.value.slice(0, selectionStart) +
              newValue.toString().padStart(2, '0') +
              target.value.slice(selectionEnd)
          )

          target.setSelectionRange(selectionStart, selectionEnd)

          break
        }
        case 'ArrowRight': {
          event.preventDefault()
          event.stopPropagation()

          if (selectionStart < 3) {
            target.setSelectionRange(3, 5)
          } else {
            target.setSelectionRange(5, 7)
          }
          break
        }
        case 'ArrowLeft': {
          event.preventDefault()
          event.stopPropagation()

          if (selectionStart >= 5) {
            target.setSelectionRange(3, 5)
          } else {
            target.setSelectionRange(0, 2)
          }
          break
        }
        case '0':
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9': {
          event.preventDefault()
          event.stopPropagation()

          const digit = parseInt(key, 10)

          if (selectionStart < 3) {
            // Hour segment (01–12).
            const parsedSegment = parseInt(selectedSegment, 10)

            if (digit <= 2 && parsedSegment === 1) {
              target.value = setValue('1' + key + target.value.substring(2))
              target.setSelectionRange(3, 5)
            } else {
              target.value = setValue('0' + key + target.value.substring(2))

              // 0 or 1 may be the first digit of a two-digit hour, so keep the
              // cursor; anything else is an unambiguous hour, so advance.
              if (digit === 0 || digit === 1) {
                target.setSelectionRange(selectionStart, selectionEnd)
              } else {
                target.setSelectionRange(3, 5)
              }
            }
          } else if (selectionStart < 5) {
            // Minute segment (00–59). A minute is entered as two digits: a tens
            // place (0–5) followed by a units place (0–9). The first digit is
            // held in a transient " D" segment until the units digit arrives.
            if (selectedSegment.includes(' ')) {
              const tens = parseInt(selectedSegment, 10)

              target.value = setValue(
                target.value.substring(0, 3) +
                  String(tens) +
                  key +
                  target.value.substring(5)
              )

              target.setSelectionRange(5, 7)
            } else if (digit <= 5) {
              target.value =
                target.value.substring(0, 3) +
                ' ' +
                key +
                target.value.substring(5)

              target.setSelectionRange(selectionStart, selectionEnd)
            } else {
              // 6–9 can't be a tens digit, so treat it as 0X (e.g. 7 → 07).
              target.value = setValue(
                target.value.substring(0, 3) +
                  '0' +
                  key +
                  target.value.substring(5)
              )

              target.setSelectionRange(5, 7)
            }
          }

          break
        }
        case 'a':
        case 'A': {
          if (selectionStart >= 5) {
            event.preventDefault()
            event.stopPropagation()

            target.value = setValue(target.value.substring(0, 5) + 'AM')

            target.setSelectionRange(5, 7)
            break
          }

          break
        }
        case 'p':
        case 'P': {
          if (selectionStart >= 5) {
            event.preventDefault()
            event.stopPropagation()

            target.value = setValue(target.value.substring(0, 5) + 'PM')

            target.setSelectionRange(5, 7)
            break
          }

          break
        }
        case 'Backspace': {
          event.preventDefault()
          event.stopPropagation()

          if (selectedSegment === '--') {
            if (selectionStart >= 5) {
              target.setSelectionRange(3, 5)
            } else if (selectionStart >= 3) {
              target.setSelectionRange(0, 2)
            }

            break
          }

          target.value =
            target.value.substring(0, selectionStart) +
            '--' +
            target.value.substring(selectionEnd)

          target.setSelectionRange(selectionStart, selectionEnd)

          break
        }
        case 'Enter':
        case 'Tab': {
          break
        }
        default: {
          event.preventDefault()
          event.stopPropagation()
        }
      }
    },
    [formFieldContext, setValue]
  )

  const handleKeyUp = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
    (event) => {
      if (
        ['ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft'].includes(event.key)
      ) {
        event.preventDefault()
        event.stopPropagation()
      }
    },
    []
  )

  const handleSelect = useCallback<React.ReactEventHandler<HTMLInputElement>>(
    (event) => {
      event.preventDefault()
      const target = event.target as HTMLInputElement

      if (!target.selectionStart || !target.selectionEnd) {
        target.setSelectionRange(0, 2)
        return
      }

      if (target.selectionStart === target.selectionEnd) {
        if (target.selectionStart < 3) {
          target.setSelectionRange(0, 2)
        } else if (target.selectionStart < 5) {
          target.setSelectionRange(3, 5)
        } else {
          target.setSelectionRange(5, 7)
        }
      }
    },
    []
  )

  useImperativeHandle(ref, () => {
    return {
      focus() {
        if (inputRef.current) {
          inputRef.current.focus()
        }
      },
    }
  }, [])

  useImperativeHandle(
    formFieldContext.formElementRef,
    () => {
      return {
        focus() {
          if (inputRef.current) {
            inputRef.current.focus()
          }
        },
      }
    },
    []
  )

  return (
    <div
      className={
        isFocused || isActive ? 'FormInput FormInput_focus' : 'FormInput'
      }
      style={{
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor:
          isFocused || isActive
            ? theme.borderColorFocus.val
            : theme.borderColor.val,
        borderRadius: 8,
        padding: '8px 12px',
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        cursor: 'text',
        transition: 'border-color 0.15s ease',
      }}
      onClick={() => inputRef.current?.focus()}
    >
      <input
        type='text'
        ref={inputRef}
        style={{
          fontFamily: 'Inter',
          fontWeight: 400,
          color: theme.color.val,
          fontSize: 16,
          lineHeight: 0,
          margin: 0,
          padding: 0,
          outline: 'none',
          border: 'none',
          width: 80,
          background: 'transparent',
        }}
        value={formattedValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onSelect={handleSelect}
      />
    </div>
  )
})
FormTimePicker.displayName = 'FormTimePicker'
